import { cn } from "@omni/utils/styles";
import { format } from "date-fns";
import React, { HTMLInputAutoCompleteAttribute, useMemo } from "react";
import { FieldValues, Path } from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";
import { getFieldValue } from "@/components/Form/RHF/Utils/form.helpers";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

const MIN_MAX_DATE_FORMAT = "yyyy-MM-dd";

export type DateFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    placeholder?: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    ref?: React.Ref<HTMLInputElement>;
    min?: Date;
    max?: Date;
};

const DateField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    placeholder,
    autoComplete,
    disabled,
    hideLabel,
    hideMicroCopy,
    optionalIndication,
    requiredIndication,
    min: minDate,
    max: maxDate,
    className,
    ref
}: DateFieldProps<T>) => {
    const { handleOnAutofill } = useAutofill<T>(name);

    const { min, max } = useMemo(
        () => ({
            min: minDate ? format(minDate, MIN_MAX_DATE_FORMAT) : undefined,
            max: maxDate ? format(maxDate, MIN_MAX_DATE_FORMAT) : undefined
        }),
        [minDate, maxDate]
    );

    return (
        <BaseField<T, Extract<Path<T>, string>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
            optionalIndication={optionalIndication}
            requiredIndication={requiredIndication}
        >
            {({ field, fieldState, isLoading }) => {
                return (
                    <Input
                        {...field}
                        value={getFieldValue(field, isLoading, "")}
                        className={cn(className)}
                        type={"date"}
                        id={name}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        disabled={disabled}
                        ref={ref}
                        min={min}
                        max={max}
                        onAutofill={handleOnAutofill}
                    />
                );
            }}
        </BaseField>
    );
};

export { DateField };
